import React from 'react';
import { Box, Container, Typography, Paper, useTheme, alpha } from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';

const Privacy = () => {
  const theme = useTheme();

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: theme.palette.primary.main,
          color: 'white',
          py: { xs: 6, md: 8 },
          mb: 6,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.4))',
            zIndex: 1,
          },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url(/privacy-hero.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transform: 'scale(1.1)',
            transition: 'transform 0.3s ease-out',
            '&:hover': {
              transform: 'scale(1.15)',
            },
          }}
        />
        <Container maxWidth="lg">
          <Box
            sx={{
              position: 'relative',
              zIndex: 2,
              textAlign: 'center',
              maxWidth: 1000,
              mx: 'auto',
              px: 3,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mb: 3,
              }}
            >
              <SecurityIcon 
                sx={{ 
                  fontSize: { xs: '3rem', md: '4rem' },
                  color: 'white',
                  opacity: 0.9,
                }}
              />
            </Box>
            <Typography
              variant="h1"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2.5rem', md: '4rem' },
                mb: 3,
                textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                color: 'white',
                letterSpacing: '-0.02em',
              }}
            >
              Privacy Policy
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                lineHeight: 1.6,
                maxWidth: 800,
                mx: 'auto',
                fontWeight: 'normal',
                textShadow: '1px 1px 2px rgba(0,0,0,0.2)',
                color: 'white',
                fontSize: { xs: '1.1rem', md: '1.3rem' },
              }}
            >
              We value your privacy and are committed to protecting your personal information
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Content Section */}
      <Container maxWidth="md" sx={{ mb: 8 }}>
        <Paper
          elevation={2}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 3,
            backgroundColor: '#fff',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              backgroundColor: theme.palette.primary.main,
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
            },
          }}
        >
          <Box sx={{ '& > *:not(:last-child)': { mb: 4 } }}>
            <Box>
              <Typography variant="h5" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 700, mb: 3 }}>
                1. Information We Collect
              </Typography>
              <Typography paragraph sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                We collect information that you provide directly to us, including when you create an account,
                make a purchase, sign up for our newsletter, or contact us. This may include:
              </Typography>
              <Box
                component="ul"
                sx={{
                  pl: 2,
                  mb: 2,
                  '& li': {
                    color: 'text.secondary',
                    mb: 1,
                    '&::marker': {
                      color: theme.palette.primary.main,
                    },
                  },
                }}
              >
                <li>Name and contact information</li>
                <li>Billing and shipping address</li>
                <li>Payment information</li>
                <li>Order history</li>
                <li>Communication preferences</li>
              </Box>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 700, mb: 3 }}>
                2. How We Use Your Information
              </Typography>
              <Typography paragraph sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                We use the information we collect to:
              </Typography>
              <Box
                component="ul"
                sx={{
                  pl: 2,
                  mb: 2,
                  '& li': {
                    color: 'text.secondary',
                    mb: 1,
                    '&::marker': {
                      color: theme.palette.primary.main,
                    },
                  },
                }}
              >
                <li>Process your orders and payments</li>
                <li>Communicate with you about your orders</li>
                <li>Send you marketing communications (with your consent)</li>
                <li>Improve our products and services</li>
                <li>Comply with legal obligations</li>
              </Box>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 700, mb: 3 }}>
                3. Information Sharing
              </Typography>
              <Typography paragraph sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                We do not sell your personal information to third parties. We may share your information with:
              </Typography>
              <Box
                component="ul"
                sx={{
                  pl: 2,
                  mb: 2,
                  '& li': {
                    color: 'text.secondary',
                    mb: 1,
                    '&::marker': {
                      color: theme.palette.primary.main,
                    },
                  },
                }}
              >
                <li>Service providers who assist in our operations</li>
                <li>Payment processors</li>
                <li>Shipping partners</li>
                <li>Legal authorities when required by law</li>
              </Box>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 700, mb: 3 }}>
                4. Security
              </Typography>
              <Typography paragraph sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                We implement appropriate technical and organizational measures to protect your personal
                information. However, no security system is impenetrable, and we cannot guarantee the
                security of our systems 100%.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 700, mb: 3 }}>
                5. Your Rights
              </Typography>
              <Typography paragraph sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                You have the right to:
              </Typography>
              <Box
                component="ul"
                sx={{
                  pl: 2,
                  mb: 2,
                  '& li': {
                    color: 'text.secondary',
                    mb: 1,
                    '&::marker': {
                      color: theme.palette.primary.main,
                    },
                  },
                }}
              >
                <li>Access your personal information</li>
                <li>Correct inaccurate information</li>
                <li>Request deletion of your information</li>
                <li>Opt-out of marketing communications</li>
                <li>Request a copy of your data</li>
              </Box>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 700, mb: 3 }}>
                6. Cookies
              </Typography>
              <Typography paragraph sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                We use cookies and similar technologies to improve your browsing experience, analyze site
                traffic, and understand where our visitors come from. You can control cookies through your
                browser settings.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 700, mb: 3 }}>
                7. Contact Us
              </Typography>
              <Typography paragraph sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                If you have any questions about this Privacy Policy or our practices, please contact us at:
              </Typography>
              <Box sx={{ pl: 2, color: 'text.secondary', lineHeight: 1.7 }}>
                Email: privacy@knackshop.com<br />
                Address: [Your Business Address]<br />
                Phone: [Your Phone Number]
              </Box>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 700, mb: 3 }}>
                8. Updates to This Policy
              </Typography>
              <Typography paragraph sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                We may update this Privacy Policy from time to time. We will notify you of any changes by
                posting the new Privacy Policy on this page and updating the effective date.
              </Typography>
            </Box>

            <Typography 
              variant="body2" 
              sx={{ 
                mt: 6, 
                pt: 4, 
                borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                color: 'text.secondary',
                textAlign: 'center',
              }}
            >
              Last updated: February 10, 2025
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Privacy;
